import { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ImageOff, Pencil, Trash2, Search } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { mediaUrl, IMG } from '../lib/mediaUrl';
import { deleteProduct } from '../lib/deleteProduct';
import { useAuth } from '../context/AuthContext';
import WishlistButton from '../components/WishlistButton';
import ConfirmDialog from '../components/ConfirmDialog';
import { useSeo } from '../lib/useSeo';

function money(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

// Minimal storefront (Chase 2026-07-22): the Antigravity particle field that
// used to sit behind this grid is gone. Just the grid — several items per row
// on every screen (two-up on phones via index.css), clean cards, no effects.
export default function ShopPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  useSeo({
    title: 'Shop · Shiny Vault LGS',
    description:
      'Browse the vault — Pokemon & TCG singles, sealed product, ETBs, graded slabs, Japanese cards, and accessories. New arrivals added often.',
    image: 'https://shinyvaultlgs.com/og-image.jpg',
    url: 'https://shinyvaultlgs.com/shop',
  });
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeLanguage, setActiveLanguage] = useState(null);
  const [activeCondition, setActiveCondition] = useState('');
  const [activeRarity, setActiveRarity] = useState('');
  const [activeSet, setActiveSet] = useState('');
  const [sort, setSort] = useState('newest');
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const searchRef = useRef(null);
  const [loading, setLoading] = useState(true);

  // Carry a search term in from the header (?q=…), even when already on /shop
  // (the page doesn't remount, so react to the param change). ?focus=1 drops
  // the cursor in the box.
  useEffect(() => {
    const q = searchParams.get('q');
    if (q !== null) setSearch(q);
    if (searchParams.get('focus') === '1' && searchRef.current) searchRef.current.focus();
  }, [searchParams]);
  // Staff-only inline management: which product is pending delete confirmation,
  // and whether the delete is in flight. Guests never see any of this.
  const [confirmProduct, setConfirmProduct] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const goEdit = (e, product) => {
    // These buttons live inside the product <Link> card — don't follow it.
    e.preventDefault();
    e.stopPropagation();
    navigate(`/admin/products/${product.id}`);
  };

  const askDelete = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteError('');
    setConfirmProduct(product);
  };

  const confirmDelete = async () => {
    if (!confirmProduct) return;
    setDeleting(true);
    const { error } = await deleteProduct(confirmProduct.id);
    setDeleting(false);
    if (error) { setDeleteError(error.message); return; }
    setProducts((prev) => prev.filter((p) => p.id !== confirmProduct.id));
    setConfirmProduct(null);
  };

  useEffect(() => {
    Promise.all([
      // Explicit column list, not select('*') — cost_cents is internal-only
      // (what we paid, never shown to shoppers) and RLS is row-level, not
      // column-level, so a select('*') here would ship it straight to the
      // browser for anyone to read off the network tab.
      supabase.from('products').select('id, name, slug, price_cents, quantity_available, category_id, language, condition, rarity, set_name, product_media(storage_path, sort_order)').eq('status', 'active').order('created_at', { ascending: false }),
      supabase.from('categories').select('*').order('sort_order'),
    ]).then(([{ data: prods }, { data: cats }]) => {
      setProducts(prods || []);
      setCategories(cats || []);
      setLoading(false);
    });
  }, []);

  const languages = useMemo(
    () => Array.from(new Set(products.map((p) => p.language).filter(Boolean))),
    [products]
  );
  // Distinct values drive the filter dropdowns; each only renders when there's
  // more than one option (same rule as language), so filters appear on their
  // own as staff fill the fields in.
  const conditions = useMemo(
    () => Array.from(new Set(products.map((p) => p.condition).filter(Boolean))),
    [products]
  );
  const rarities = useMemo(
    () => Array.from(new Set(products.map((p) => p.rarity).filter(Boolean))).sort(),
    [products]
  );
  const sets = useMemo(
    () => Array.from(new Set(products.map((p) => p.set_name).filter(Boolean))).sort(),
    [products]
  );

  const coverPhotoUrl = (product) => {
    const photos = (product.product_media || []).slice().sort((a, b) => a.sort_order - b.sort_order);
    if (!photos[0]) return null;
    return mediaUrl(photos[0].storage_path, IMG.card);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = products
      // Hide sold-out items from shoppers; staff still see them (dimmed, with
      // SOLD OUT) so they can click in and restock.
      .filter((p) => isAdmin || p.quantity_available > 0)
      .filter((p) => !activeCategory || p.category_id === activeCategory)
      .filter((p) => !activeLanguage || p.language === activeLanguage)
      .filter((p) => !activeCondition || p.condition === activeCondition)
      .filter((p) => !activeRarity || p.rarity === activeRarity)
      .filter((p) => !activeSet || p.set_name === activeSet)
      .filter((p) => !q || p.name.toLowerCase().includes(q));
    if (sort === 'price-asc') return [...list].sort((a, b) => a.price_cents - b.price_cents);
    if (sort === 'price-desc') return [...list].sort((a, b) => b.price_cents - a.price_cents);
    return list; // 'newest' — already ordered by created_at desc from the query
  }, [products, activeCategory, activeLanguage, activeCondition, activeRarity, activeSet, search, sort, isAdmin]);

  if (loading) return <div className="sv-container" style={{ padding: 60 }}>Loading…</div>;

  return (
    <div className="sv-page-top sv-page-bottom">
      <div className="sv-container">
        <h1 style={{ fontSize: '1.8rem', fontWeight: 900, marginBottom: 20 }}>Shop</h1>

        <div style={{ position: 'relative', maxWidth: 460, marginBottom: 24 }}>
          <Search size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
          <input
            ref={searchRef}
            className="sv-input"
            type="search"
            placeholder="Search products…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search products"
            style={{ width: '100%', paddingLeft: 40 }}
          />
        </div>

        {categories.length > 0 && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 28, flexWrap: 'wrap' }}>
            <button
              onClick={() => setActiveCategory(null)}
              className={activeCategory === null ? 'sv-btn' : 'sv-btn-outline'}
              style={{ padding: '7px 16px', fontSize: '0.85rem' }}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveCategory(c.id)}
                className={activeCategory === c.id ? 'sv-btn' : 'sv-btn-outline'}
                style={{ padding: '7px 16px', fontSize: '0.85rem' }}
              >
                {c.name}
              </button>
            ))}
          </div>
        )}

        {languages.length > 1 && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 28, flexWrap: 'wrap' }}>
            <button
              onClick={() => setActiveLanguage(null)}
              className={activeLanguage === null ? 'sv-btn' : 'sv-btn-outline'}
              style={{ padding: '7px 16px', fontSize: '0.85rem' }}
            >
              All languages
            </button>
            {languages.map((lang) => (
              <button
                key={lang}
                onClick={() => setActiveLanguage(lang)}
                className={activeLanguage === lang ? 'sv-btn' : 'sv-btn-outline'}
                style={{ padding: '7px 16px', fontSize: '0.85rem' }}
              >
                {lang}
              </button>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 28 }}>
          <select className="sv-input" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort" style={{ width: 'auto', padding: '7px 12px', fontSize: '0.85rem' }}>
            <option value="newest">Newest</option>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
          </select>
          {conditions.length > 1 && (
            <select className="sv-input" value={activeCondition} onChange={(e) => setActiveCondition(e.target.value)} aria-label="Condition" style={{ width: 'auto', padding: '7px 12px', fontSize: '0.85rem' }}>
              <option value="">All conditions</option>
              {conditions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          {rarities.length > 1 && (
            <select className="sv-input" value={activeRarity} onChange={(e) => setActiveRarity(e.target.value)} aria-label="Rarity" style={{ width: 'auto', padding: '7px 12px', fontSize: '0.85rem' }}>
              <option value="">All rarities</option>
              {rarities.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          )}
          {sets.length > 1 && (
            <select className="sv-input" value={activeSet} onChange={(e) => setActiveSet(e.target.value)} aria-label="Set" style={{ width: 'auto', padding: '7px 12px', fontSize: '0.85rem' }}>
              <option value="">All sets</option>
              {sets.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </div>

        {filtered.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', padding: '60px 0', textAlign: 'center' }}>
            {search.trim() ? `No matches for "${search.trim()}".` : 'Nothing listed yet — check back soon.'}
          </div>
        ) : (
          <div className="sv-product-grid">
            {filtered.map((p) => {
              const soldOut = p.quantity_available <= 0;
              const photoUrl = coverPhotoUrl(p);
              return (
                <Link key={p.id} to={`/product/${p.slug}`} className="sv-card" style={{ overflow: 'hidden', display: 'block' }}>
                  <div
                    style={{
                      position: 'relative',
                      aspectRatio: '3 / 4', background: 'var(--bg-elevated)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)',
                      padding: 12,
                    }}
                  >
                    <WishlistButton product={p} />
                    {photoUrl ? (
                      <img src={photoUrl} alt={p.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                      <ImageOff size={28} />
                    )}
                  </div>
                  <div style={{ padding: 14 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 4 }}>{p.name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: 800 }}>{money(p.price_cents)}</span>
                      {soldOut ? (
                        <span style={{ fontSize: '0.72rem', color: 'var(--danger)', fontWeight: 700 }}>SOLD OUT</span>
                      ) : (
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 700 }}>
                          {p.quantity_available} in stock
                        </span>
                      )}
                    </div>
                    {isAdmin && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                        <button
                          type="button"
                          onClick={(e) => goEdit(e, p)}
                          className="sv-btn-outline"
                          style={{ flex: 1, padding: '8px 10px', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                        >
                          <Pencil size={14} /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={(e) => askDelete(e, p)}
                          className="sv-btn-outline"
                          style={{ flex: 1, padding: '8px 10px', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: 'var(--danger)', borderColor: 'var(--danger)' }}
                        >
                          <Trash2 size={14} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {confirmProduct && (
        <ConfirmDialog
          title="Delete this item?"
          message={
            deleteError
              ? `Couldn't delete: ${deleteError}`
              : `"${confirmProduct.name}" will be permanently removed from the shop, along with its photos. Past orders that included it are kept. This can't be undone.`
          }
          confirmLabel="Delete item"
          busy={deleting}
          onConfirm={confirmDelete}
          onCancel={() => { if (!deleting) { setConfirmProduct(null); setDeleteError(''); } }}
        />
      )}
    </div>
  );
}
